/**
 * Trades Screen
 *
 * Displays portfolio trades chronologically (newest first) with pagination.
 * No longer grouped by symbol.
 * NOW THEME-AWARE
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  SafeAreaView,
  FlatList,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { showError } from '../../utils/alert';
import { usePortfolioNavigate } from '../../navigation/PortfolioQualifierContext';
import type { Trade } from '../../services/portfolioApi';

interface TradeApiResponse {
  trades: Trade[];
  total_count: number;
  has_more: boolean;
  offset: number;
  limit: number;
}

// API call for paginated trades
async function getTradesPaginated(
  offset: number = 0,
  limit: number = 20
): Promise<TradeApiResponse> {
  const API_URL = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8000';
  const token = await require('../../services/tokenStorage').getToken();

  const response = await fetch(`${API_URL}/api/v1/trades?offset=${offset}&limit=${limit}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch trades');
  }

  return response.json();
}

export function TradesScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const navigateToPortfolio = usePortfolioNavigate();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);

  const PAGE_SIZE = 20;

  const loadData = useCallback(
    async (offset: number = 0, append: boolean = false) => {
      if (offset === 0) {
        setLoading(true);
      }

      try {
        const data = await getTradesPaginated(offset, PAGE_SIZE);

        if (append) {
          setTrades((prev) => [...prev, ...data.trades]);
        } else {
          setTrades(data.trades.reverse()); // Reverse to show newest first
        }

        setHasMore(data.has_more);
        setTotalCount(data.total_count);
        setCurrentOffset(data.offset + data.limit);
      } catch (error: any) {
        console.error('Failed to load trades:', error?.message);
      } finally {
        setLoading(false);
      }
    },
    [PAGE_SIZE]
  );

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const data = await getTradesPaginated(currentOffset, PAGE_SIZE);

      // Append new trades (reversed to maintain chronological order within the batch)
      const newTrades = data.trades.reverse();
      setTrades((prev) => [...prev, ...newTrades]);

      setHasMore(data.has_more);
      setCurrentOffset(data.offset + data.limit);
    } catch (error: any) {
      console.error('Failed to load more trades:', error?.message);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, currentOffset, PAGE_SIZE]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData(0, false);
    setRefreshing(false);
  }, [loadData]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  const handleTradePress = useCallback(
    (trade: Trade) => {
      if (trade.position_id) {
        navigateToPortfolio('PositionDetail', {
          positionId: trade.position_id,
        });
      } else {
        showError(
          t('errors.positionNotFound', { defaultValue: 'Position not found for this trade' })
        );
      }
    },
    [navigateToPortfolio, t]
  );

  const handleEditPress = useCallback(
    (trade: Trade) => {
      navigateToPortfolio('AddTrade', { tradeId: trade.id });
    },
    [navigateToPortfolio]
  );

  const getTransactionTypeColor = (transactionType?: string) => {
    switch (transactionType) {
      case 'buy':
      case 'deposit':
        return theme.colors.success;
      case 'sell':
      case 'withdrawal':
        return theme.colors.error;
      case 'split':
      case 'consolidate':
      case 'bonus':
        return theme.colors.primary;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getTransactionTypeLabel = (transactionType?: string) => {
    switch (transactionType) {
      case 'buy':
        return t('portfolio.buy');
      case 'sell':
        return t('portfolio.sell');
      case 'deposit':
        return t('portfolio.deposit');
      case 'withdrawal':
        return t('portfolio.withdrawal');
      case 'split':
        return t('portfolio.split');
      case 'consolidate':
        return t('portfolio.consolidate');
      case 'bonus':
        return t('portfolio.bonus');
      default:
        return transactionType || t('portfolio.buy');
    }
  };

  const renderTradeItem = useCallback(
    ({ item }: { item: Trade }) => {
      const tradeDate = new Date(item.trade_datetime);
      const isPositiveQuantity = parseFloat(item.quantity) >= 0;

      return (
        <TouchableOpacity
          style={[
            styles.tradeItem,
            { backgroundColor: theme.colors.backgroundElevated, borderColor: theme.colors.border },
          ]}
          onPress={() => handleTradePress(item)}
          activeOpacity={0.7}
        >
          <View style={styles.tradeHeader}>
            <View style={styles.tradeHeaderLeft}>
              <Text style={[styles.symbolText, { color: theme.colors.text }]}>{item.symbol}</Text>
              <View
                style={[
                  styles.transactionTypeBadge,
                  { backgroundColor: getTransactionTypeColor(item.transaction_type) + '20' },
                ]}
              >
                <Text
                  style={[
                    styles.transactionTypeText,
                    { color: getTransactionTypeColor(item.transaction_type) },
                  ]}
                >
                  {getTransactionTypeLabel(item.transaction_type)}
                </Text>
              </View>
            </View>
            <Text style={[styles.tradeDateText, { color: theme.colors.textTertiary }]}>
              {tradeDate.toLocaleDateString()}{' '}
              {tradeDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>

          <View style={styles.tradeDetails}>
            <View style={styles.tradeDetailRow}>
              <Text style={[styles.tradeDetailLabel, { color: theme.colors.textSecondary }]}>
                {t('portfolio.quantity')}
              </Text>
              <Text
                style={[
                  styles.tradeDetailValue,
                  { color: isPositiveQuantity ? theme.colors.success : theme.colors.error },
                ]}
              >
                {isPositiveQuantity ? '+' : ''}
                {item.quantity}
              </Text>
            </View>

            <View style={styles.tradeDetailRow}>
              <Text style={[styles.tradeDetailLabel, { color: theme.colors.textSecondary }]}>
                {t('portfolio.price')}
              </Text>
              <Text style={[styles.tradeDetailValue, { color: theme.colors.text }]}>
                {item.currency} {parseFloat(item.trade_price).toFixed(2)}
              </Text>
            </View>

            <View style={styles.tradeDetailRow}>
              <Text style={[styles.tradeDetailLabel, { color: theme.colors.textSecondary }]}>
                {t('portfolio.realizedPL')}
              </Text>
              <Text
                style={[
                  styles.tradeDetailValue,
                  parseFloat(item.realized_pl) >= 0
                    ? { color: theme.colors.success }
                    : { color: theme.colors.error },
                ]}
              >
                {item.currency} {parseFloat(item.realized_pl) >= 0 ? '+' : ''}
                {parseFloat(item.realized_pl).toFixed(2)}
              </Text>
            </View>
          </View>

          {item.has_comments && (
            <View style={styles.commentBadge}>
              <Text style={[styles.commentBadgeText, { color: theme.colors.primary }]}>
                💬 {item.comment_count}
              </Text>
            </View>
          )}

          <TouchableOpacity
            style={styles.editButton}
            onPress={() => handleEditPress(item)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Text style={[styles.editButtonText, { color: theme.colors.textTertiary }]}>✏️</Text>
          </TouchableOpacity>
        </TouchableOpacity>
      );
    },
    [theme, handleTradePress, handleEditPress, t]
  );

  const renderListEmptyComponent = useCallback(() => {
    return (
      <View style={styles.emptyContainer}>
        <Text style={[styles.emptyText, { color: theme.colors.text }]}>
          {t('portfolio.noTradesFound', { defaultValue: 'No trades found' })}
        </Text>
        <Text style={[styles.emptySubtext, { color: theme.colors.textTertiary }]}>
          {t('portfolio.addFirstTrade', { defaultValue: 'Add your first trade to get started' })}
        </Text>
      </View>
    );
  }, [theme, t]);

  const renderListFooter = useCallback(() => {
    if (!loading && !hasMore && trades.length > 0) {
      return (
        <View style={styles.listFooter}>
          <Text style={[styles.footerText, { color: theme.colors.textTertiary }]}>
            {t('portfolio.allTradesLoaded', {
              count: totalCount,
              defaultValue: `All ${totalCount} trades loaded`,
            })}
          </Text>
        </View>
      );
    }

    if (loadingMore) {
      return (
        <View style={styles.listFooter}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Text style={[styles.footerText, { color: theme.colors.textSecondary }]}>
            {t('portfolio.loadingMore', { defaultValue: 'Loading more...' })}
          </Text>
        </View>
      );
    }

    return null;
  }, [loading, loadingMore, hasMore, trades.length, totalCount, theme, t]);

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
            {t('portfolio.loadingTrades')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header with trade count */}
      <View
        style={[
          styles.header,
          { backgroundColor: theme.colors.surface, borderBottomColor: theme.colors.border },
        ]}
      >
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          {t('portfolio.allTrades')}
        </Text>
        <Text style={[styles.headerSubtitle, { color: theme.colors.textSecondary }]}>
          {totalCount > 0 ? `${totalCount} ${t('portfolio.trades')}` : ''}
        </Text>
      </View>

      {/* Trade List */}
      <FlatList
        data={trades}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderTradeItem}
        ListEmptyComponent={renderListEmptyComponent}
        ListFooterComponent={renderListFooter}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={theme.colors.primary}
          />
        }
        onEndReached={() => {
          if (hasMore && !loadingMore) {
            loadMore();
          }
        }}
        onEndReachedThreshold={0.5}
        contentContainerStyle={styles.listContent}
      />

      {/* Manual Load More Button (shown when has trades and has more) */}
      {hasMore && trades.length > 0 && !loadingMore && (
        <View
          style={[
            styles.loadMoreContainer,
            { backgroundColor: theme.colors.surface, borderTopColor: theme.colors.border },
          ]}
        >
          <TouchableOpacity
            style={[
              styles.loadMoreButton,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
              },
            ]}
            onPress={loadMore}
          >
            <Text style={[styles.loadMoreButtonText, { color: theme.colors.primary }]}>
              {t('portfolio.loadMore', { defaultValue: 'Load More' })}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    header: {
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    headerTitle: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    headerSubtitle: {
      fontSize: theme.typography.fontSizeSm,
      marginTop: 4,
    },
    centerContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 48,
    },
    loadingText: {
      marginTop: 12,
      fontSize: theme.typography.fontSizeMd,
    },
    listContent: {
      padding: 16,
      paddingTop: 8,
      paddingBottom: 8,
      flexGrow: 1,
    },
    tradeItem: {
      padding: 14,
      borderRadius: theme.radii.md,
      marginBottom: 12,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      position: 'relative',
    },
    tradeHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    tradeHeaderLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    symbolText: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    transactionTypeBadge: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: theme.radii.sm,
    },
    transactionTypeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: 'uppercase',
    },
    tradeDateText: {
      fontSize: theme.typography.fontSizeXs,
    },
    tradeDetails: {
      gap: 8,
    },
    tradeDetailRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    tradeDetailLabel: {
      fontSize: theme.typography.fontSizeSm,
    },
    tradeDetailValue: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    commentBadge: {
      position: 'absolute',
      top: 14,
      right: 40,
    },
    commentBadgeText: {
      fontSize: theme.typography.fontSizeXs,
    },
    editButton: {
      position: 'absolute',
      top: 14,
      right: 14,
    },
    editButtonText: {
      fontSize: 16,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32,
      marginTop: 100,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
    },
    emptySubtext: {
      fontSize: theme.typography.fontSizeSm,
    },
    listFooter: {
      paddingVertical: 24,
      alignItems: 'center',
    },
    footerText: {
      fontSize: theme.typography.fontSizeSm,
    },
    loadMoreContainer: {
      padding: theme.spacing.md,
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    loadMoreButton: {
      paddingVertical: theme.spacing.md,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    loadMoreButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
